// /admin/v1/commerce/skus — 商业 SKU 管理 (钻石包 / 会员订阅 / 单次解锁 / 实体加购).
// Mirrors backend handlers/commerce/admin.go (MountAdmin → /commerce/skus*).

import { list, post, put } from './client';
import type { ListResult } from './client';

/** skus.kind CHECK ('one_time','subscription','diamond_pack','physical_addon'). */
export type SKUKind = 'one_time' | 'subscription' | 'diamond_pack' | 'physical_addon';

/** skus.status CHECK ('draft','active','archived'). */
export type SKUStatus = 'draft' | 'active' | 'archived';

export interface SKU {
  id: number;
  app_id: number;
  code: string;
  name: string;
  kind: SKUKind;
  base_price_cents: number;
  /** 钻石包 = 充入钻石数; 会员订阅 = 每周期赠钻数 (0 = 不赠). */
  diamond_amount: number;
  /** 订阅周期 (天): 7 = 周卡, 30 = 月卡, 365 = 年卡. 非订阅为 0. */
  cycle_days: number;
  auto_renew: boolean;
  package_id?: number | null;
  status: SKUStatus;
  config?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

export interface ListSKUsQuery {
  /** 必填 — 后端按 App 列 SKU. */
  app_id: number;
  kind?: SKUKind;
}

export function listSKUs(q: ListSKUsQuery): Promise<ListResult<SKU>> {
  return list<SKU>('/admin/v1/commerce/skus', q);
}

/** Create + Update 共用 — 后端 UpdateSKU 也要求全量字段 (binding:required). */
export interface SKUInput {
  app_id: number;
  code: string;
  name: string;
  kind: SKUKind;
  /** 分. 后端 binding:required → 必须 > 0. */
  base_price_cents: number;
  diamond_amount?: number;
  cycle_days?: number;
  auto_renew?: boolean;
  package_id?: number | null;
  status?: SKUStatus;
  config?: Record<string, unknown>;
}

export function createSKU(input: SKUInput): Promise<SKU> {
  return post<SKU>('/admin/v1/commerce/skus', input);
}

export function updateSKU(id: number, input: SKUInput): Promise<SKU> {
  return put<SKU>(`/admin/v1/commerce/skus/${id}`, input);
}

// ============== 渠道定价 (sku_prices, 按价格组 × 渠道覆盖 base price) ==============

export type PriceChannel = 'wx_miniapp' | 'dy_miniapp' | 'h5' | 'ios' | 'android' | 'all';

export interface SKUPrice {
  id: number;
  sku_id: number;
  price_group_id: number;
  channel: PriceChannel;
  price_cents: number;
}

export interface UpsertSKUPriceInput {
  price_group_id: number;
  channel: PriceChannel;
  price_cents: number;
}

export function upsertSKUPrice(skuId: number, input: UpsertSKUPriceInput): Promise<SKUPrice> {
  return post<SKUPrice>(`/admin/v1/commerce/skus/${skuId}/prices`, input);
}

export interface PriceGroup {
  id: number;
  app_id?: number | null;
  code: string;
  name: string;
  description?: string | null;
}

export function listPriceGroups(appId?: number): Promise<ListResult<PriceGroup>> {
  return list<PriceGroup>('/admin/v1/commerce/price-groups', appId ? { app_id: appId } : undefined);
}
